import { useEffect, useRef } from 'react';
import type { Channel } from 'stream-chat';
import { useChatContext } from 'stream-chat-react';

import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { dispatch, useSelector } from '@/stores';
import { openChatChannel } from '../api';
import { clearOpenChatRequest } from '../slices';
import type { ChatOpenChannelPayload, StreamChatType } from '../types';

const getApiMessage = (error: any) => {
  const data = error?.response?.data;

  if (typeof data?.message === 'string') return data.message;
  if (typeof data?.error === 'string') return data.error;
  if (typeof error?.message === 'string') return error.message;

  return 'Something went wrong';
};

const getErrorTitle = (error: any) =>
  error?.response?.status === 403
    ? 'Chat access denied'
    : 'Unable to open chat';

export const ChatOpenRequestHandler = () => {
  const { client, setActiveChannel } = useChatContext<StreamChatType>();
  const pendingOpenChatRequest = useSelector(
    (state) => state.chatStream.pendingOpenChatRequest
  );
  const processingRequestId = useRef<string | null>(null);
  const { errorMsg } = useSnackbarMsg();

  useEffect(() => {
    if (!pendingOpenChatRequest) return;
    if (processingRequestId.current === pendingOpenChatRequest.requestId) {
      return;
    }

    processingRequestId.current = pendingOpenChatRequest.requestId;

    const openChat = async () => {
      try {
        const { requestId: _requestId, ...payload } = pendingOpenChatRequest;
        const { channel_id: channelId, channel_type: channelType } =
          await openChatChannel(payload as ChatOpenChannelPayload);
        const channel = client.channel(
          channelType,
          channelId
        ) as Channel<StreamChatType>;

        await channel.watch();
        if (client.userID) {
          await channel.show(client.userID).catch(() => undefined);
        }

        setActiveChannel(channel);
      } catch (error) {
        errorMsg(new Error(getApiMessage(error)), getErrorTitle(error));
      } finally {
        dispatch(clearOpenChatRequest());
        processingRequestId.current = null;
      }
    };

    void openChat();
  }, [client, errorMsg, pendingOpenChatRequest, setActiveChannel]);

  return null;
};
